import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, Eye, EyeOff, Users, ExternalLink, X, Upload, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  slug: "", title_pt: "", title_en: "", title_es: "",
  subtitle_pt: "", body_pt: "",
  date_str: "", date_iso: "", start_time: "", end_time: "",
  location: "", image_url: "",
  price_eur: 0, currency: "eur", active: true, has_meal: false,
  community_id: "",
};

const slugify = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

function formatDuration(minutes) {
  if (minutes == null || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

function computeDurationMinutes(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return mins;
}

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // null | EMPTY
  const [editing, setEditing] = useState({});      // map slug -> draft

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/events`, { withCredentials: true });
      setEvents(r.data.items || []);
      setEditing({});
    } catch (e) { toast.error("Erro a carregar."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveExisting = async (slug) => {
    const draft = editing[slug];
    if (!draft) return;
    try {
      await axios.patch(`${API}/admin/events/${slug}`, draft, { withCredentials: true });
      toast.success("Atualizado.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Apagar evento "${ev.title_pt || ev.slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/events/${ev.slug}`, { withCredentials: true });
      toast.success("Apagado.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const create = async (e) => {
    e?.preventDefault?.();
    const payload = { ...creating, slug: slugify(creating.slug || creating.title_pt), price_eur: Number(creating.price_eur) || 0 };
    if (!payload.slug || !payload.title_pt) { toast.error("Preencha título e slug."); return; }
    try {
      await axios.post(`${API}/admin/events`, payload, { withCredentials: true });
      toast.success("Evento criado.");
      setCreating(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const editField = (slug, key, val) => setEditing((m) => ({ ...m, [slug]: { ...(m[slug] || {}), [key]: val } }));
  const draftValue = (ev, key) => (editing[ev.slug] && key in editing[ev.slug]) ? editing[ev.slug][key] : ev[key];

  return (
    <div className="px-6 py-10 lg:px-12 lg:py-14" data-testid="admin-events-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Eventos</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Gestão de eventos.</h1>
          <span className="accent-rule mt-6 block" />
        </div>
        <button onClick={() => setCreating(EMPTY)} disabled={!!creating}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630] disabled:opacity-50"
          data-testid="event-create-button">
          <Plus className="h-4 w-4" /> Novo Evento
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <form onSubmit={create} className="mt-8 bg-white border border-[hsl(var(--border))] p-6 space-y-4" data-testid="event-create-form">
          <div className="flex items-center justify-between">
            <h2 className="display-heading text-2xl">Novo evento</h2>
            <button type="button" onClick={() => setCreating(null)} className="text-[hsl(var(--muted-foreground))]"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Título (PT) *"><input required value={creating.title_pt} onChange={(e) => setCreating({...creating, title_pt: e.target.value, slug: creating.slug || slugify(e.target.value)})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="event-title-pt" /></F>
            <F label="Slug (URL) *" hint="ex.: retiro-fatima-2027"><input required value={creating.slug} onChange={(e) => setCreating({...creating, slug: slugify(e.target.value)})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm font-mono" data-testid="event-slug" /></F>
            <F label="Título (EN)"><input value={creating.title_en} onChange={(e) => setCreating({...creating, title_en: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
            <F label="Título (ES)"><input value={creating.title_es} onChange={(e) => setCreating({...creating, title_es: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
            <F label="Data (texto)"><input value={creating.date_str} onChange={(e) => setCreating({...creating, date_str: e.target.value})} placeholder="15-17 Abril 2027" className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
            <F label="Data ISO" hint="YYYY-MM-DD · ativa a contagem decrescente"><input type="date" value={creating.date_iso} onChange={(e) => setCreating({...creating, date_iso: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="event-date-iso" /></F>
            <F label="Hora de início" hint="formato 24h (HH:MM)"><input type="time" value={creating.start_time} onChange={(e) => setCreating({...creating, start_time: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="event-start-time" /></F>
            <F label="Hora de fim" hint={formatDuration(computeDurationMinutes(creating.start_time, creating.end_time)) ? `Duração: ${formatDuration(computeDurationMinutes(creating.start_time, creating.end_time))}` : "formato 24h (HH:MM)"}><input type="time" value={creating.end_time} onChange={(e) => setCreating({...creating, end_time: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="event-end-time" /></F>
            <F label="Local"><input value={creating.location} onChange={(e) => setCreating({...creating, location: e.target.value})} placeholder="Fátima, Portugal" className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
            <F label="Preço (€)"><input type="number" min="0" step="1" value={creating.price_eur} onChange={(e) => setCreating({...creating, price_eur: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="event-price" /></F>
          </div>
          <div className="pt-2">
            <ImageUploadField
              slug={creating.slug || "novo"}
              currentUrl={creating.image_url}
              onUploaded={(url) => setCreating({...creating, image_url: url})}
              onManualChange={(url) => setCreating({...creating, image_url: url})}
            />
          </div>
          <F label="Subtítulo (PT)"><input value={creating.subtitle_pt} onChange={(e) => setCreating({...creating, subtitle_pt: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
          <F label="Descrição (PT)" hint="Use linhas em branco para separar parágrafos."><textarea rows={6} value={creating.body_pt} onChange={(e) => setCreating({...creating, body_pt: e.target.value})} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
          <label className="flex items-center gap-2"><input type="checkbox" checked={creating.active} onChange={(e) => setCreating({...creating, active: e.target.checked})} className="h-4 w-4 accent-[hsl(var(--primary))]" /> <span className="text-sm">Ativo (visível no site)</span></label>
          <label className="flex items-center gap-2" data-testid="event-create-has-meal-label"><input type="checkbox" checked={creating.has_meal} onChange={(e) => setCreating({...creating, has_meal: e.target.checked})} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="event-create-has-meal" /> <span className="text-sm">Inclui refeição (mostra campo de restrições alimentares no RSVP)</span></label>
          <div className="flex justify-end gap-3 pt-3 border-t border-[hsl(var(--border))]">
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="event-create-submit">Criar evento</button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="mt-8 space-y-3">
        {loading ? <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p> :
          events.length === 0 ? <p className="text-[hsl(var(--muted-foreground))]">Sem eventos. Crie o primeiro.</p> :
          events.map((ev) => (
            <div key={ev.slug} className={`bg-white border border-[hsl(var(--border))] p-5 ${!ev.active ? "opacity-60" : ""}`} data-testid={`event-row-${ev.slug}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="display-heading text-xl">{ev.title_pt || ev.slug}</h3>
                    {!ev.active && <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--muted))] px-1.5 py-0.5">Inativo</span>}
                  </div>
                  <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))] font-mono">{ev.slug}</div>
                  {(ev.date_str || ev.location || ev.start_time) && (
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                      {[
                        ev.date_str,
                        (ev.start_time || ev.end_time) ? [ev.start_time, ev.end_time].filter(Boolean).join(" – ") : null,
                        ev.duration_minutes ? `(${formatDuration(ev.duration_minutes)})` : null,
                        ev.location,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div className="mt-2 inline-flex items-center gap-2 text-[11px]" data-testid={`event-rsvp-counter-${ev.slug}`}>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] tracking-wide">
                      <Users className="h-3 w-3" />
                      {ev.rsvp_count || 0} {ev.rsvp_count === 1 ? "inscrito" : "inscritos"}
                    </span>
                    {ev.price_eur > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 tracking-wide">
                        ✓ {ev.rsvp_paid_count || 0} pagos
                      </span>
                    )}
                  </div>
                </div>
                <div className="inline-flex items-center gap-3 shrink-0">
                  <Link to={`/admin/inscricoes?event=${ev.slug}`} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline" data-testid={`event-view-rsvps-${ev.slug}`}>
                    <Users className="h-3.5 w-3.5" /> Inscritos
                  </Link>
                  <a href={`/eventos/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                    <ExternalLink className="h-3.5 w-3.5" /> Ver
                  </a>
                  <button onClick={() => remove(ev)} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`event-delete-${ev.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-4 gap-3 items-end">
                <F label="Preço (€)"><input type="number" min="0" step="1" defaultValue={ev.price_eur ?? 0} onChange={(e) => editField(ev.slug, "price_eur", Number(e.target.value) || 0)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-price-${ev.slug}`} /></F>
                <F label="Data (texto)"><input defaultValue={ev.date_str || ""} onChange={(e) => editField(ev.slug, "date_str", e.target.value)} placeholder="ex.: 20 Set 2026" className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
                <F label="Data ISO" hint="YYYY-MM-DD · usada na contagem decrescente"><input type="date" defaultValue={ev.date_iso || ""} onChange={(e) => editField(ev.slug, "date_iso", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-date-iso-${ev.slug}`} /></F>
                <F label="Local"><input defaultValue={ev.location || ""} onChange={(e) => editField(ev.slug, "location", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-3 items-end">
                <F label="Hora de início"><input type="time" defaultValue={ev.start_time || ""} onChange={(e) => editField(ev.slug, "start_time", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-start-time-${ev.slug}`} /></F>
                <F label="Hora de fim"><input type="time" defaultValue={ev.end_time || ""} onChange={(e) => editField(ev.slug, "end_time", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-end-time-${ev.slug}`} /></F>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Duração</label>
                  <div className="px-3 py-2 text-sm bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]" data-testid={`event-duration-${ev.slug}`}>
                    {formatDuration(
                      computeDurationMinutes(
                        draftValue(ev, "start_time") || ev.start_time,
                        draftValue(ev, "end_time") || ev.end_time,
                      ),
                    ) || <span className="text-[hsl(var(--muted-foreground))] italic text-xs">— defina início e fim —</span>}
                  </div>
                </div>
              </div>

              <label className="mt-3 inline-flex items-center gap-2" data-testid={`event-has-meal-label-${ev.slug}`}>
                <input type="checkbox" defaultChecked={!!ev.has_meal}
                  onChange={(e) => editField(ev.slug, "has_meal", e.target.checked)}
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  data-testid={`event-has-meal-${ev.slug}`} />
                <span className="text-sm">Inclui refeição
                  <span className="ml-1 text-[11px] text-[hsl(var(--muted-foreground))]">(mostra campo de restrições alimentares no RSVP)</span>
                </span>
              </label>

              <details className="mt-4 border-t border-[hsl(var(--border))] pt-4 group" data-testid={`event-edit-full-${ev.slug}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))] font-medium">
                  <span>Editar título, descrição e imagem</span>
                  <span className="text-xs transition-transform group-open:rotate-180">▼</span>
                </summary>
                <div className="mt-5 grid sm:grid-cols-3 gap-3">
                  <F label="Título PT"><input defaultValue={ev.title_pt || ""} onChange={(e) => editField(ev.slug, "title_pt", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-title-pt-${ev.slug}`} /></F>
                  <F label="Título EN"><input defaultValue={ev.title_en || ""} onChange={(e) => editField(ev.slug, "title_en", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
                  <F label="Título ES"><input defaultValue={ev.title_es || ""} onChange={(e) => editField(ev.slug, "title_es", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" /></F>
                </div>
                <div className="mt-3">
                  <F label="Subtítulo PT"><input defaultValue={ev.subtitle_pt || ""} onChange={(e) => editField(ev.slug, "subtitle_pt", e.target.value)} className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`event-subtitle-pt-${ev.slug}`} /></F>
                </div>
                <div className="mt-3">
                  <F label="Corpo / Descrição (PT)" hint="Quebras de linha separam parágrafos.">
                    <textarea
                      rows={5}
                      defaultValue={ev.body_pt || ""}
                      onChange={(e) => editField(ev.slug, "body_pt", e.target.value)}
                      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm resize-vertical"
                      data-testid={`event-body-pt-${ev.slug}`}
                    />
                  </F>
                </div>
                <div className="mt-3">
                  <ImageUploadField
                    slug={ev.slug}
                    currentUrl={draftValue(ev, "image_url")}
                    onUploaded={(url) => editField(ev.slug, "image_url", url)}
                    onManualChange={(url) => editField(ev.slug, "image_url", url)}
                  />
                </div>
              </details>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" defaultChecked={!!ev.active} onChange={(e) => editField(ev.slug, "active", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                  {ev.active ? <Eye className="h-3.5 w-3.5 text-emerald-600" /> : <EyeOff className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />}
                  {ev.active ? "Ativo (visível no site)" : "Inativo"}
                </label>
                <button onClick={() => saveExisting(ev.slug)} disabled={!editing[ev.slug]} className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630] disabled:opacity-40" data-testid={`event-save-${ev.slug}`}>
                  <Save className="h-3.5 w-3.5" /> Guardar
                </button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

const F = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);

function ImageUploadField({ slug, currentUrl, onUploaded, onManualChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Use JPG, PNG ou WEBP."); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 5 MB)."); return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/admin/events/upload-image`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data.url);
      toast.success("Imagem carregada. Clique 'Guardar' para aplicar.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar imagem.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
        Imagem do evento
      </label>
      <div className="grid sm:grid-cols-[140px_1fr] gap-4 items-start">
        <div className="w-[140px] h-[100px] bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] flex items-center justify-center overflow-hidden">
          {currentUrl ? (
            <img src={currentUrl} alt="" className="w-full h-full object-cover" data-testid={`event-image-preview-${slug}`} />
          ) : (
            <ImageIcon className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
          )}
        </div>
        <div className="space-y-2">
          <input
            type="url"
            key={`url-${currentUrl || "empty"}`}
            defaultValue={currentUrl || ""}
            placeholder="URL da imagem (https://…)"
            onChange={(e) => onManualChange(e.target.value)}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm font-mono"
            data-testid={`event-image-url-${slug}`}
          />
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid={`event-image-upload-input-${slug}`}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-wider hover:bg-[hsl(var(--primary))] hover:text-white transition-colors disabled:opacity-50"
              data-testid={`event-image-upload-button-${slug}`}
            >
              <Upload className="h-3.5 w-3.5" /> {uploading ? "A carregar…" : "Carregar imagem"}
            </button>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">JPG/PNG/WEBP · máx 5 MB</p>
          </div>
        </div>
      </div>
    </div>
  );
}
